/**
 * Opciones de `VitePWA` de Fermin Staff (006 · T002, research R-21).
 *
 * Extraídas **tal cual** de `vite.config.ts` para que una prueba unitaria pueda fijarlas sin
 * levantar Vite (`tests/unit/pwa-config.test.ts`, US3). Cambiar algo aquí cambia el manifest y el
 * service worker que se instalan en los teléfonos: `display: 'standalone'`, colores Crema, icono
 * `maskable`, `autoUpdate` con `skipWaiting`/`clientsClaim` y, sobre todo, **nunca** caché de
 * `/api` (sin `runtimeCaching`; wiring 006 §0.7).
 */
import type { VitePWAOptions } from 'vite-plugin-pwa';

/** Crema (design-system §8.1): `theme_color` y `background_color` del manifest. */
export const THEME_COLOR = '#F5F2EC';

// PWA (implementation-notes 002 §4): SW autoUpdate, fallback SPA y NUNCA caché de /api.
export const STAFF_PWA_OPTIONS = {
  registerType: 'autoUpdate',
  injectRegister: 'auto',
  // En dev no se registra el service worker: evita cachés fantasma mientras se desarrolla.
  devOptions: { enabled: false },
  // Iconos y favicon los produce T002 (`public/icons/*`, `public/favicon.svg`, `public/apple-touch-icon.png`).
  includeAssets: ['favicon.svg', 'apple-touch-icon.png'],
  manifest: {
    id: '/',
    name: 'Fermin',
    short_name: 'Fermin',
    description: 'Marca tu asistencia con Fermin.',
    lang: 'es-PE',
    start_url: '/',
    scope: '/',
    display: 'standalone',
    orientation: 'portrait',
    theme_color: THEME_COLOR,
    background_color: THEME_COLOR,
    icons: [
      { src: 'icons/icon-192.png', sizes: '192x192', type: 'image/png' },
      { src: 'icons/icon-512.png', sizes: '512x512', type: 'image/png' },
      {
        src: 'icons/maskable-512.png',
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
      },
    ],
  },
  workbox: {
    /**
     * 004 · T003: `wasm` entra en el precache para que el decodificador de QR
     * (`zxing-wasm`, research R-1) esté disponible sin red. El `.wasm` se sirve **desde el
     * propio origen** —`src/camera/decoder.worker.ts` lo importa con `?url`, así que Vite lo
     * emite en `dist/assets/` y este patrón lo recoge—, nunca desde un CDN: con el service
     * worker instalado, una descarga cruzada rompería la marcación en cuanto falle la red.
     */
    globPatterns: ['**/*.{js,css,html,svg,png,woff2,wasm}'],
    // El `.wasm` del decodificador pesa ~950 KB; el tope por defecto de Workbox es 2 MiB.
    maximumFileSizeToCacheInBytes: 4 * 1024 * 1024,
    navigateFallback: '/index.html',
    // Las llamadas a la API nunca caen al fallback SPA ni se cachean (sin runtimeCaching de /api).
    navigateFallbackDenylist: [/^\/api\//],
    cleanupOutdatedCaches: true,
    clientsClaim: true,
    skipWaiting: true,
  },
} satisfies Partial<VitePWAOptions>;
